import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, Link2, Unlink, Send, RefreshCw, ExternalLink, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TelegramConfig = () => {
  const [botInfo, setBotInfo] = useState(null);
  const [status, setStatus] = useState({ linked: false, chat_id: null });
  const [chatId, setChatId] = useState('');
  const [recentChats, setRecentChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [testing, setTesting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [botResp, statusResp] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/telegram/bot-info`),
        axios.get(`${BACKEND_URL}/api/telegram/status`),
      ]);
      setBotInfo(botResp.data);
      setStatus(statusResp.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const scanForChats = async () => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/telegram/check-updates`);
      setRecentChats(data.updates || []);
      if (data.updates?.length === 0) {
        setMessage('No se encontraron conversaciones. Asegurate de enviar /start al bot primero.');
      }
    } catch (error) {
      setMessage('Error al buscar conversaciones');
    } finally {
      setScanning(false);
    }
  };

  const linkTelegram = async (selectedChatId) => {
    const idToLink = selectedChatId || chatId;
    if (!idToLink) return;
    setLinking(true);
    setMessage('');
    try {
      await axios.post(`${BACKEND_URL}/api/telegram/link`, { chat_id: idToLink });
      setMessage('Telegram vinculado exitosamente! Revisa tu chat.');
      setStatus({ linked: true, chat_id: idToLink });
      setChatId('');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Error al vincular');
    } finally {
      setLinking(false);
    }
  };

  const unlinkTelegram = async () => {
    if (!window.confirm('Desvincular Telegram? Dejaras de recibir alertas.')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/telegram/unlink`);
      setStatus({ linked: false, chat_id: null });
      setMessage('Telegram desvinculado');
    } catch (error) {
      setMessage('Error al desvincular');
    }
  };

  const sendTestAlert = async () => {
    setTesting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/telegram/test-alert`);
      setMessage('Alerta de prueba enviada! Revisa tu Telegram.');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Error al enviar');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Cargando configuracion de Telegram...</div>;
  }

  return (
    <div className="space-y-6" data-testid="telegram-config">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#0088cc] flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Notificaciones por Telegram</h3>
          <p className="text-sm text-slate-500">Recibe alertas de vencimientos y tareas en tu Telegram</p>
        </div>
      </div>

      {/* Status */}
      {status.linked ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-800">Telegram vinculado</span>
          </div>
          <p className="text-sm text-emerald-700">Chat ID: <code className="bg-emerald-100 px-1.5 py-0.5 rounded text-xs">{status.chat_id}</code></p>
          <div className="text-xs text-emerald-600 space-y-0.5">
            <p>Recibiras alertas de:</p>
            <p>- Vencimientos que vencen HOY</p>
            <p>- Vencimientos que vencen MANANA</p>
            <p>- Vencimientos que vencen en 3 dias</p>
            <p>- Vencimientos y tareas atrasadas</p>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={sendTestAlert} disabled={testing} className="text-xs" data-testid="test-alert-btn">
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {testing ? 'Enviando...' : 'Enviar alerta de prueba'}
            </Button>
            <Button variant="outline" size="sm" onClick={unlinkTelegram} className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="unlink-telegram-btn">
              <Unlink className="w-3.5 h-3.5 mr-1.5" />
              Desvincular
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Instructions */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
            <p className="text-sm font-semibold text-slate-700">Para vincular tu Telegram:</p>
            <ol className="text-sm text-slate-600 space-y-2 list-decimal pl-5">
              <li>
                Abre Telegram y busca el bot{' '}
                {botInfo && (
                  <a
                    href={`https://t.me/${botInfo.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 font-medium hover:underline inline-flex items-center gap-1"
                    data-testid="bot-link"
                  >
                    @{botInfo.username} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </li>
              <li>Enviale el mensaje <code className="bg-slate-200 px-1.5 py-0.5 rounded text-xs font-mono">/start</code></li>
              <li>Vuelve aqui y haz clic en <strong>"Buscar mi chat"</strong></li>
            </ol>
          </div>

          {/* Scan button */}
          <Button onClick={scanForChats} disabled={scanning} className="bg-[#0088cc] hover:bg-[#006fa1] text-white" data-testid="scan-chats-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Buscando...' : 'Buscar mi chat'}
          </Button>

          {/* Recent chats */}
          {recentChats.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Conversaciones encontradas:</p>
              {recentChats.map((chat) => (
                <div key={chat.chat_id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:border-blue-300 transition-colors">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{chat.first_name || 'Usuario'}</p>
                    {chat.username && <p className="text-xs text-slate-500">@{chat.username}</p>}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => linkTelegram(chat.chat_id)}
                    disabled={linking}
                    className="bg-[#1F3864] hover:bg-[#172b4d] text-white text-xs"
                    data-testid={`link-chat-${chat.chat_id}`}
                  >
                    <Link2 className="w-3.5 h-3.5 mr-1.5" />
                    Vincular
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Manual input */}
          <div className="border-t border-slate-200 pt-4">
            <p className="text-xs text-slate-500 mb-2">O ingresa el Chat ID manualmente:</p>
            <div className="flex gap-2">
              <Input
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="Ej: 123456789"
                className="max-w-xs text-sm"
                data-testid="manual-chat-id"
              />
              <Button
                onClick={() => linkTelegram()}
                disabled={!chatId || linking}
                className="bg-[#1F3864] hover:bg-[#172b4d] text-white text-xs"
                data-testid="link-manual-btn"
              >
                {linking ? 'Vinculando...' : 'Vincular'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {message && (
        <div className={`text-sm p-3 rounded-lg ${message.includes('exitosa') || message.includes('enviada') ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`} data-testid="telegram-message">
          {message}
        </div>
      )}
    </div>
  );
};

export default TelegramConfig;
